import React, { ReactNode, useState, useRef, useLayoutEffect } from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import CssBaseline from "@mui/material/CssBaseline";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { AppBar } from "@mui/material";
import RequireAuth from "../context/RequireAuth";
import NotificationPrompt from "../prompts/NotificationPrompt";
import ResponsiveDrawer from "./ResponsiveDrawer";
import HttpErrorProvider from "../context/HttpErrorProvider";
import useIsMobile from "../hooks/useIsMobile";
import CheckTerms, { hasAcceptedTermsOfService } from "../context/CheckTerms";
import { AccountType } from "protogen/common_pb";
import { ReactComponent as FayeIcon } from "../../icons/FayeBrand.svg";
import ContextAggregator, {
  ContextComponent,
} from "../helpers/ContextAggregator";
import LinkRouter from "../navigation/LinkRouter";
import { useAppBarSx } from "../context/AppBarContext";
import ActiveCallModule from "../phone/ActiveCallModule";
import FloatingTimerModule from "../time-tracking/FloatingTimerModule";
import ActiveVideoCallModule from "../video/ActiveVideoCallModule";
import PersistentVideoRoom from "../video/PersistentVideoRoom";
import { showNewPasswordModal } from "../context/NewPasswordPrompt";
import {
  isMobileAndroid,
  isMobileiOS,
  isStandaloneWebapp,
} from "../../common/utils";
import WebAppInstallPrompt from "../prompts/WebAppInstallPrompt";
import { PAGE_FRAME_HEIGHT } from "common/constants";
import AuthService from "services/auth";
import { SHARED_PATHS } from "./constants";
import { PROD_MEMBER_DOMAIN } from "components/context/constants";
import { VideoCallContextProvider } from "components/context/VideoCallContext";
import MemberUnpaidBanner from "components/member/MemberUnpaidBanner";
import MemberDigitalUpgradeBanner from "components/member/MemberDigitalUpgradeBanner";
import DeactivatedAccessBanner from "components/member/DeactivatedAccessBanner";
import { useUnpaidBanner } from "components/context/UnpaidBannerContext";
export const FayeAppBar = ({ rightAction }: { rightAction?: ReactNode }) => {
  const { sx } = useAppBarSx();
  return (
    <AppBar
      sx={{
        height: "50px",
        background: "#FDFAF7",
        boxShadow: "none",
        borderBottom: "1px solid #E8E8E8",
        ...sx,
      }}
    >
      <Toolbar>
        <LinkRouter to="/">
          <FayeIcon
            style={{
              height: "26px",
              margin: "12px 0px",
            }}
          />
        </LinkRouter>
        <Box sx={{ flexGrow: 1 }} />
        {rightAction}
      </Toolbar>
    </AppBar>
  );
};

export const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

export const DrawerContent = ({
  isPersistent,
  open,
  setOpen,
  content,
}: {
  isPersistent: boolean;
  open: boolean;
  setOpen: (b: boolean) => void;
  content: React.ReactNode;
}) => {
  const location = useLocation();
  const isMobile = useIsMobile();
  let selectedTab = location.pathname;
  if (selectedTab.endsWith("/")) {
    selectedTab = selectedTab.slice(0, -1);
  }
  const setClosed = () => setOpen(false);
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        justifyContent: "space-between",
        paddingBottom: "10px",
        backgroundColor: "#FDFAF7",
      }}
    >
      {typeof content === "function" &&
        content({ open, isPersistent, selectedTab, setClosed, isMobile })}
    </Box>
  );
};

export const NoBadge = ({ children }: { children?: ReactNode }) =>
  children ? <>{children}</> : null;

type NavigationLayoutProps = {
  drawerContent: React.ReactNode;
  loginRedirectPath: string;
  tosAccountType?: AccountType;
  // Set of wrapper components that should be added within RequireAuth.
  authedContexts?: ContextComponent[];
  MobileBadgerComponent?: React.ComponentType;
  hideInstallPrompt?: boolean;
};

export default ({
  drawerContent,
  loginRedirectPath,
  tosAccountType,
  authedContexts,
  MobileBadgerComponent = NoBadge,
  hideInstallPrompt = false,
}: NavigationLayoutProps) => {
  const location = useLocation();
  const currentUser = AuthService.getCurrentUser();
  const isSharedPath = SHARED_PATHS.some((path) =>
    location.pathname.startsWith(path),
  );
  const [open, setOpen] = useState(false);
  const drawerWidth = 240;
  const persistentNavWidth = 240;
  const isMobile = useIsMobile();
  const useAppBar = isMobile;
  const supportMobileApps = isMobileAndroid() || isMobileiOS();
  const handleOpen = (o: boolean) => {
    if (!useAppBar) {
      return;
    }
    setOpen(o);
  };
  const prioritizedModal = () => {
    if (
      tosAccountType === AccountType.MEMBER &&
      (!hasAcceptedTermsOfService() || showNewPasswordModal())
    ) {
      return <Navigate to="/account-setup" replace />;
    } else if (!hasAcceptedTermsOfService() && tosAccountType) {
      return <CheckTerms accountType={tosAccountType} />;
    }
    if (
      !isStandaloneWebapp() &&
      isMobile &&
      supportMobileApps &&
      tosAccountType &&
      !hideInstallPrompt
    ) {
      return <WebAppInstallPrompt accountType={tosAccountType} />;
    }
    return null;
  };
  const drawerOpen = !useAppBar || open;

  const { shouldShowBanner, shouldShowDigitalUpgradeBanner } =
    useUnpaidBanner();
  const hasBanner = shouldShowBanner || shouldShowDigitalUpgradeBanner;

  const mainBoxRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // In a bad spot where we have a banner but use calc for page layout.
  // Blame the former as a bad pattern - but I don't want to touch this and
  // end up fixing pages for days.  @TODO: @kegami
  useLayoutEffect(() => {
    if (!hasBanner || !sentinelRef.current || !mainBoxRef.current) return;
    const measure = () => {
      if (!sentinelRef.current || !mainBoxRef.current) return;
      const top = Math.round(sentinelRef.current.getBoundingClientRect().top);
      mainBoxRef.current.style.setProperty("--page-content-top", `${top}px`);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(mainBoxRef.current);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [hasBanner]);

  return !currentUser &&
    window.location.hostname === PROD_MEMBER_DOMAIN &&
    isSharedPath ? (
    <HttpErrorProvider>
      <VideoCallContextProvider>
        <ActiveVideoCallModule />
        <PersistentVideoRoom />
        <Outlet />
      </VideoCallContextProvider>
    </HttpErrorProvider>
  ) : (
    <RequireAuth loginRedirectPath={loginRedirectPath}>
      <HttpErrorProvider>
        {/* For a standalone app, maybe show the notification prompt.
            This also handles refreshing tokens and does some work even when not presented to the user.
         */}
        {(tosAccountType === AccountType.ADVISOR ||
          (tosAccountType === AccountType.MEMBER && isStandaloneWebapp())) && (
          <NotificationPrompt accountType={tosAccountType} />
        )}
        {/* Note that the odds of conflicting with the notification modal
            are quite low since it requires being in the standalone app and logged in.
        */}
        {prioritizedModal()}
        <ContextAggregator contexts={authedContexts}>
          {tosAccountType === AccountType.ADVISOR && <ActiveCallModule />}
          {tosAccountType === AccountType.ADVISOR && <FloatingTimerModule />}
          <ActiveVideoCallModule />
          <PersistentVideoRoom />
          <CssBaseline />
          {useAppBar && (
            <FayeAppBar
              rightAction={
                <MobileBadgerComponent>
                  <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    onClick={() => handleOpen(true)}
                    sx={{
                      ...(open && { display: "none" }),
                    }}
                  >
                    <MenuIcon sx={{ color: "#198282" }} />
                  </IconButton>
                </MobileBadgerComponent>
              }
            />
          )}
          <ResponsiveDrawer
            open={drawerOpen}
            setOpen={(o) => handleOpen(o)}
            drawerWidth={drawerWidth}
          >
            <DrawerContent
              isPersistent={!useAppBar}
              open={drawerOpen}
              setOpen={(o) => handleOpen(o)}
              content={drawerContent}
            />
          </ResponsiveDrawer>
          <DeactivatedAccessBanner
            sx={{
              margin: useAppBar
                ? "58px 6px 6px 6px"
                : `12px 12px 12px ${persistentNavWidth}px`,
              "@media print": {
                marginLeft: 0,
              },
            }}
          />
          <Box
            ref={mainBoxRef}
            sx={{
              marginLeft: `${useAppBar ? 0 : persistentNavWidth}px`,
              // Drawer is hidden on print.
              "@media print": {
                marginLeft: 0,
              },
              background: "white",
              "&:not(:has(.two-panel-grid-container))": {
                borderRadius: !isMobile ? "16px 0 0 0" : undefined,
                borderTop: "1px solid #EAEBEC",
                borderLeft: "1px solid #EAEBEC",
              },
              marginTop: isMobile ? "0" : "12px",
              minHeight: PAGE_FRAME_HEIGHT,
            }}
          >
            {/* Needed to make space for app bar. */}
            {useAppBar && <DrawerHeader />}
            <MemberUnpaidBanner />
            <MemberDigitalUpgradeBanner />
            <div ref={sentinelRef} />
            <Outlet />
          </Box>
        </ContextAggregator>
      </HttpErrorProvider>
    </RequireAuth>
  );
};
